import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Snackbar, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import * as CompanyService from '@/services/accountService/companyService';
import type { CompanyInfo } from '@/services/accountService/companyService';
import {
  downloadFsExcelTemplate,
  importExcel,
  FS_IMPORT_ASSESSABLE_YEAR_EXISTS,
  triggerFsBlobDownload,
  type FsExcelTemplateVariant,
} from '@/services/auditService/auditFsAnnualService';
import type { AliasLockedNode } from '@/services/auditService/auditFsAnnualService';
import CompanyDetail from '@/components/company-info/company/CompanyDetail';
import { FileUploadDialog } from '@/pages/components/common';
import {useFsCreateCtx} from '@/pages/components/audit/finance-statement/create-stepper/context';
import {ensureFsCreateTxStarted} from '@/services/auditService/fsCreateTxService';
import {
  formatContentDateFeedback,
  normalizeContentSheetDates,
} from '@/pages/components/audit/finance-statement/create-stepper/normalizeContentDates';

const PANEL_HEIGHT = 'calc(100vh - 260px)';

export default function StepACo() {
  const { state, dispatch, fileUploadActionRef, crValidateRef } = useFsCreateCtx();

  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [companyInput, setCompanyInput] = useState('');
  const companyIdleRef = useRef<any>(null);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [inlineCreateOpen, setInlineCreateOpen] = useState(false);
  const [uploadPanelOpen, setUploadPanelOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('Content sheet imported successfully');
  const [aliasLockedNodes, setAliasLockedNodes] = useState<AliasLockedNode[]>([]);
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const [downloading, setDownloading] = useState(false);
  const saveRef = useRef<(() => Promise<boolean>) | null>(null);

  const selectedCompany = state.selectedCompany;
  const hasImportedContent = !!state.contentData;
  const companyId = (selectedCompany as any)?.companyId ?? (selectedCompany as any)?.id ?? null;

  // Sync upload panel state to FsCreateStepper footer
  useEffect(() => {
    dispatch({ type: 'set_upload_panel_open', payload: uploadPanelOpen });
    if (!uploadPanelOpen) {
      dispatch({ type: 'set_upload_panel_state', payload: { canSubmit: false, uploading: false } });
    }
  }, [uploadPanelOpen, dispatch]);

  // Sync inline create state to parent
  useEffect(() => {
    dispatch({
      type: 'set_is_creating_company',
      payload: inlineCreateOpen || state.companyEditOnStep0 || uploadPanelOpen,
    });
  }, [inlineCreateOpen, state.companyEditOnStep0, uploadPanelOpen, dispatch]);

  // Initialize / update create button visibility
  useEffect(() => {
    setShowCreateCompany(!selectedCompany && !inlineCreateOpen && !uploadPanelOpen);
  }, [selectedCompany, inlineCreateOpen, uploadPanelOpen]);

  // If a company gets selected, ensure inline create is closed
  useEffect(() => {
    if (selectedCompany) setInlineCreateOpen(false);
  }, [selectedCompany]);

  // Close upload panel after successful import
  useEffect(() => {
    if (hasImportedContent) setUploadPanelOpen(false);
  }, [hasImportedContent]);

  // Load companies on mount
  useEffect(() => {
    const run = async () => {
      setCompanyLoading(true);
      setCompanyError(null);
      try {
        const list = await CompanyService.querySelectionList();
        setCompanies(list || []);
      } catch (e: any) {
        setCompanyError(e?.message || 'Failed to load companies');
      } finally {
        setCompanyLoading(false);
      }
    };
    run();
  }, []);

  const reloadAndSelectNewest = useCallback(async (): Promise<number | null> => {
    try {
      setCompanyLoading(true);
      const list = await CompanyService.querySelectionList();
      const options = Array.isArray(list) ? list : [];
      setCompanies(options as any);
      const newest = options.reduce((acc: any, cur: any) =>
        acc == null || (cur.companyId ?? cur.id) > (acc.companyId ?? acc.id) ? cur : acc, null);
      if (newest) {
        dispatch({ type: 'set_company', payload: newest });
        const id = newest.companyId ?? newest.id;
        return id != null ? Number(id) : null;
      }
      return null;
    } finally {
      setCompanyLoading(false);
    }
  }, [dispatch]);

  const handleUploadActionStateChange = useCallback((s: { canUpload: boolean; uploading: boolean }) => {
    dispatch({
      type: 'set_upload_panel_state',
      payload: { canSubmit: s.canUpload, uploading: s.uploading },
    });
    setImporting(s.uploading);
  }, [dispatch]);

  const handleUploadPanelClose = useCallback(() => {
    setUploadPanelOpen(false);
  }, []);

  const handleImportUpload = useCallback(async (file: File) => {
    setImportError(null);
    const xid = await ensureFsCreateTxStarted();
    dispatch({ type: 'set_seata_xid', payload: xid });
    let result;
    try {
      result = await importExcel(file, companyId);
    } catch (e: any) {
      if (e?.code === FS_IMPORT_ASSESSABLE_YEAR_EXISTS) {
        dispatch({ type: 'set_assessable_year_conflict', payload: e.message || 'Assessable year already exists.' });
        const suppressed = new Error(e.message) as Error & { suppressUploadDialogError?: boolean };
        suppressed.suppressUploadDialogError = true;
        throw suppressed;
      }
      throw e;
    }

    if (result.contentData) {
      const norm = normalizeContentSheetDates(result.contentData);
      const periodInvalid = norm.invalid.filter(
        (s) => s.startsWith('Period From') || s.startsWith('Period To'),
      );
      if (periodInvalid.length) {
        throw new Error(
          `Excel Content dates must use dd/MM/yyyy: ${periodInvalid.join('; ')}`,
        );
      }
      result = { ...result, contentData: norm.data };
      dispatch({ type: 'set_content_data', payload: norm.data });
      const tip = formatContentDateFeedback(norm);
      if (tip) {
        setSnackMsg(tip);
        setSnackOpen(true);
      } else {
        setSnackMsg('Content sheet imported successfully');
        setSnackOpen(true);
      }
    }

    dispatch({
      type: 'set_import_flags',
      payload: {
        companyMatched: result.companyMatched,
        companyNameUpdated: result.companyNameUpdated,
      },
    });

    if (result.importToken) {
      dispatch({ type: 'set_import_token', payload: result.importToken });
    }

    if (result.matchedCompany) {
      dispatch({ type: 'set_imported_company', payload: result.matchedCompany });
      dispatch({ type: 'set_company', payload: result.matchedCompany as any });
    }

    if (result.aliasLockedNodes && result.aliasLockedNodes.length > 0) {
      setAliasLockedNodes(result.aliasLockedNodes);
    }

    return result;
  }, [companyId, dispatch]);

  const handleDownloadTemplate = useCallback(async (variant: FsExcelTemplateVariant) => {
    setDownloadAnchor(null);
    if (downloading) return;
    setDownloading(true);
    setImportError(null);
    try {
      const { blob, filename } = await downloadFsExcelTemplate(variant);
      const fallback = variant === 'multi'
        ? 'AFS_template_multiyear.xlsx'
        : 'AFS_template_single.xlsx';
      triggerFsBlobDownload(blob, filename || fallback);
      setSnackMsg(variant === 'multi'
        ? 'Multi-year template downloaded'
        : 'Single-year template downloaded');
      setSnackOpen(true);
    } catch (e: any) {
      setImportError(e?.message || 'Template download failed');
    } finally {
      setDownloading(false);
    }
  }, [downloading]);

  // 内联创建、编辑或导入后由 FsCreateStepper Next 触发 CompanyDetail 保存
  useEffect(() => {
    const needsBeforeNext =
      inlineCreateOpen ||
      state.companyEditOnStep0 ||
      (hasImportedContent && !!selectedCompany);
    if (!needsBeforeNext) {
      dispatch({ type: 'set_before_next', payload: undefined });
      return () => dispatch({ type: 'set_before_next', payload: undefined });
    }
    dispatch({
      type: 'set_before_next',
      payload: async () => {
        if (!saveRef.current) return true;
        const wasCreating = inlineCreateOpen;
        const ok = await saveRef.current();
        if (!ok) return false;
        if (wasCreating) {
          const createdId = await reloadAndSelectNewest();
          // Mark wizard-created company so cancel/rollback can compensate-delete it.
          // Queried/selected companies leave wizardCreatedCompanyId null → ignored on rollback.
          dispatch({ type: 'set_wizard_created_company_id', payload: createdId });
        }
        return true;
      },
    });
    return () => dispatch({ type: 'set_before_next', payload: undefined });
  }, [hasImportedContent, inlineCreateOpen, state.companyEditOnStep0, selectedCompany, dispatch, reloadAndSelectNewest]);

  // Auto-fill from Content Sheet import
  const contentAppliedRef = useRef(false);
  useEffect(() => {
    if (contentAppliedRef.current) return;
    const cd = state.contentData;
    if (!cd) return;

    if (!state.companyMatched && !selectedCompany) {
      contentAppliedRef.current = true;
      setInlineCreateOpen(true);
    } else if (state.companyMatched && state.importedCompany) {
      contentAppliedRef.current = true;
    }
  }, [state.contentData, state.companyMatched, state.importedCompany, selectedCompany]);

  // Build initialData from contentData for CompanyDetail pre-fill (no-match / inline create)
  const contentInitialData = useMemo<Partial<CompanyInfo> | undefined>(() => {
    const cd = state.contentData;
    if (!cd) return undefined;
    const data: Partial<CompanyInfo> = {};
    if (cd.companyName) data.companyName = cd.companyName;
    if (cd.companyChineseName) data.companyChineseName = cd.companyChineseName;
    if (cd.inCorpDate) data.inCorpDate = cd.inCorpDate;
    if (cd.br) data.br = cd.br;
    return Object.keys(data).length > 0 ? data : undefined;
  }, [state.contentData]);

  const showSelectionCompany =
    !inlineCreateOpen &&
    !state.companyEditOnStep0 &&
    !hasImportedContent &&
    !uploadPanelOpen;
  const showUploadPanel = uploadPanelOpen && !hasImportedContent;
  const showCompanyDetailEdit = state.companyEditOnStep0 && !!selectedCompany && !inlineCreateOpen;

  return (
    <Box sx={{
      height: PANEL_HEIGHT,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      overflow: showUploadPanel ? 'hidden' : 'auto',
    }}>
      {showSelectionCompany && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography variant="subtitle1" fontWeight={700}>Selection Company</Typography>
            <Box flex={1} />
            <Tooltip title="Download blank AFS template (single or multi-year)">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={downloading || importing}
                  onClick={(e) => setDownloadAnchor(e.currentTarget)}
                >
                  {downloading ? <CircularProgress size={18} /> : <DownloadIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Menu
              anchorEl={downloadAnchor}
              open={Boolean(downloadAnchor)}
              onClose={() => setDownloadAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => handleDownloadTemplate('single')} disabled={downloading}>
                <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Single-year template"
                  secondary="G = current · BA = last year"
                />
              </MenuItem>
              <MenuItem onClick={() => handleDownloadTemplate('multi')} disabled={downloading}>
                <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Multi-year template"
                  secondary="B6.2: G/I/K = Year 1/2/3 → Step 4 YOA columns · B6.1: same as single-year"
                />
              </MenuItem>
            </Menu>
            <Tooltip title="Import Content Sheet (.xlsx / .xlsm) to auto-fill steps">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={importing || downloading}
                  onClick={() => setUploadPanelOpen(true)}
                >
                  {importing ? <CircularProgress size={18} /> : <UploadFileIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {companyError && <Alert severity="error" sx={{ mb: 1 }}>{companyError}</Alert>}
          {importError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setImportError(null)}>{importError}</Alert>}
          <Autocomplete
            value={selectedCompany}
            onChange={(_, v) => {
              dispatch({ type: 'set_company', payload: v as any });
              dispatch({ type: 'set_company_edit_on_step0', payload: false });
              // Queried/selected existing company — cancel rollback must ignore company
              dispatch({ type: 'set_wizard_created_company_id', payload: null });
            }}
            options={companies}
            loading={companyLoading}
            getOptionLabel={(o) => o?.companyName || ''}
            isOptionEqualToValue={(opt, val) => {
              const ok = (opt as any) || {};
              const vk = (val as any) || {};
              return (ok.companyId ?? ok.id) === (vk.companyId ?? vk.id);
            }}
            inputValue={companyInput}
            onInputChange={(_, v) => {
              setCompanyInput(v);
              if (!v || v.trim() === '') {
                setShowCreateCompany(!selectedCompany && !inlineCreateOpen && !uploadPanelOpen);
                if (companyIdleRef.current) clearTimeout(companyIdleRef.current);
                return;
              }
              setShowCreateCompany(false);
              if (companyIdleRef.current) clearTimeout(companyIdleRef.current);
              companyIdleRef.current = setTimeout(() => {
                setShowCreateCompany(!selectedCompany && !inlineCreateOpen && !uploadPanelOpen);
              }, 1200);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && showCreateCompany) {
                e.preventDefault();
                setInlineCreateOpen(true);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Company" placeholder="Select a company" InputProps={{ ...params.InputProps, endAdornment: (
                <>
                  {companyLoading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                  {showCreateCompany && (
                    <Button size="small" sx={{ ml: 0.5 }} onClick={(e) => {
                      e.stopPropagation();
                      dispatch({ type: 'set_company_edit_on_step0', payload: false });
                      setInlineCreateOpen(true);
                    }}>Create Company</Button>
                  )}
                </>
              ) }} />
            )}
          />

          {selectedCompany && (
            <Box mt={2}>
              <Stack spacing={0.5}>
                <Typography variant="body2"><b>Company Name:</b> {state.contentData?.companyName || selectedCompany.companyName}</Typography>
                <Typography variant="body2"><b>Chinese Name:</b> {state.contentData?.companyChineseName || selectedCompany.companyChineseName}</Typography>
                <Typography variant="body2"><b>Industry:</b> {selectedCompany.industryName}</Typography>
                <Typography variant="body2"><b>InCorp Date:</b> {state.contentData?.inCorpDate || selectedCompany.inCorpDate}</Typography>
                <Typography variant="body2"><b>BR:</b> {state.contentData?.br || selectedCompany.br}</Typography>
              </Stack>
            </Box>
          )}
        </>
      )}

      {showUploadPanel && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <FileUploadDialog
            embedded
            fillHeight
            hideActions
            open
            actionRef={fileUploadActionRef}
            onActionStateChange={handleUploadActionStateChange}
            onClose={handleUploadPanelClose}
            onSuccess={() => {
              setSnackMsg('Content sheet imported successfully');
              setSnackOpen(true);
            }}
            onUploadingChange={setImporting}
            onUpload={handleImportUpload}
            title="Import Content Sheet"
            accept=".xlsx,.xlsm"
            acceptLabel="Accepted: .xlsx / .xlsm · Max size: 10MB"
          />
        </Box>
      )}

      {inlineCreateOpen && (
        <Box mt={hasImportedContent ? 0 : 2}>
          <CompanyDetail
            open
            renderMode="content"
            disableRegisteredOfficeValidation
            hideRegisteredOffice
            hideIndustry
            hideActionButtons
            saveRef={saveRef}
            crValidateRef={hasImportedContent ? crValidateRef : undefined}
            initialData={contentInitialData}
            onClose={() => {
              setInlineCreateOpen(false);
            }}
          />
        </Box>
      )}

      {showCompanyDetailEdit && (
        <Box mt={2}>
          <CompanyDetail
            open
            renderMode="content"
            companyId={selectedCompany!.companyId ?? (selectedCompany as { id?: number }).id}
            disableRegisteredOfficeValidation
            hideRegisteredOffice
            hideIndustry
            hideActionButtons
            saveRef={saveRef}
            crValidateRef={hasImportedContent ? crValidateRef : undefined}
            initialData={contentInitialData}
            onClose={() => {
              dispatch({ type: 'set_company_edit_on_step0', payload: false });
            }}
          />
        </Box>
      )}

      {hasImportedContent && selectedCompany && !inlineCreateOpen && !state.companyEditOnStep0 && (
        <CompanyDetail
          open
          renderMode="content"
          companyId={selectedCompany.companyId ?? (selectedCompany as { id?: number }).id}
          disableRegisteredOfficeValidation
          hideRegisteredOffice
          hideIndustry
          hideActionButtons
          saveRef={saveRef}
          crValidateRef={crValidateRef}
          initialData={contentInitialData}
          onClose={async (payload) => {
            if (payload?.refreshed) {
              try {
                setCompanyLoading(true);
                const list = await CompanyService.querySelectionList();
                setCompanies(Array.isArray(list) ? list : []);
              } finally {
                setCompanyLoading(false);
              }
            }
          }}
        />
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={1200}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>

      <Dialog
        open={aliasLockedNodes.length > 0}
        onClose={() => { /* force user to acknowledge */ }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Locked Node Names</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            The following nodes are system-locked and cannot be renamed via Excel import. The import has skipped renaming these nodes and their original names remain unchanged. Please confirm to continue.
          </Alert>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {aliasLockedNodes.map((n) => (
              <Typography component="li" variant="body2" key={n.code} sx={{ mb: 0.5 }}>
                {n.name}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setAliasLockedNodes([])}>
            Acknowledged, Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
